import { DefaultError, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNotifications } from "hooks/notifications"
import { Creators } from "model/apiWrapper"
import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { getThreadsQueryKey } from "../utils/threads-cache"
import { FetchThreadsResponse } from "../types/thread"

interface UpdateThreadPayload {
  accountAlias: string
  threadId: string
  title: string
}

export function useUpdateThread() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()

  const { mutateAsync, isPending } = useMutation<unknown, DefaultError, UpdateThreadPayload>({
    mutationFn: ({ accountAlias, threadId, title }) => {
      return new Promise((resolve, reject) => {
        dispatch(Creators.patch(`/api/${accountAlias}/ai/chat/threads/${threadId}`, { title }, resolve, reject))
      })
    },
    onSuccess: (_, { accountAlias, threadId, title }) => {
      queryClient.setQueryData(getThreadsQueryKey(accountAlias), (old: FetchThreadsResponse) => {
        if (!old?.threads) {
          return old
        }
        return {
          threads: old.threads.map((thread) => {
            if (thread.threadId === threadId) {
              return { ...thread, title }
            }
            return thread
          }),
        }
      })
    },
    onError: () => {
      notifs.error("An unexpected error occurred while updating the thread title.")
    },
  })

  return useMemo(() => ({
    updateThread: mutateAsync,
    isUpdatingThread: isPending,
  }), [mutateAsync, isPending])
}
